import * as semver from 'semver';

/**
 * KIR-3911: the "current" released agent version — the one whose installer the
 * dashboard hands out. Baked into the frontend build as `VITE_VSIX_VERSION`
 * (sourced from the repo-root `VERSION` file; it's what makes the installer
 * filename `Kirin-<version>.pkg`). Empty in local/unconfigured builds → "current
 * is unknown", so nothing is ever colored red.
 */
export function getCurrentAgentVersion(): string | null {
  const raw = import.meta.env.VITE_VSIX_VERSION;
  return raw && raw.length > 0 ? raw : null;
}

/**
 * KIR-3911: true when `version` is strictly older than `current` (both parseable
 * as semver). Unknown/unparseable inputs are never "outdated" — we don't color
 * on uncertainty.
 */
export function isAgentVersionOutdated(
  version: string | null | undefined,
  current: string | null | undefined
): boolean {
  if (!version || !current) return false;
  const v = semver.valid(semver.coerce(version));
  const c = semver.valid(semver.coerce(current));
  if (!v || !c) return false;
  return semver.lt(v, c);
}

/**
 * KIR-3911: numeric semver ordering for agent-version sorting (so `3.9.0`
 * sorts before `3.10.0`, matching the backend's numeric least-version logic —
 * a lexical sort would invert them). Both parseable → semver compare; otherwise
 * fall back to a stable lexical compare. Empty/unparseable values sort last.
 */
export function compareAgentVersions(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const va = a ? semver.valid(semver.coerce(a)) : null;
  const vb = b ? semver.valid(semver.coerce(b)) : null;
  if (va && vb) return semver.compare(va, vb);
  // Parseable versions always precede unparseable/empty ones.
  if (va) return -1;
  if (vb) return 1;
  return (a ?? '').localeCompare(b ?? '');
}
